import { faUserFriends, faUsers } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useId, useRef, useState } from "react";

import { CertificateIcon } from "../assets/icons";
import "./SocialProof.css";

const MAX_AVATARS = 3;

const InitialsAvatar = ({ user, style }) => {
    const initial = (user.first_name || user.full_name || "?")
        .charAt(0)
        .toUpperCase();

    return (
        <span className="social_proof_avatar social_proof_avatar--initials" title={user.full_name} style={style}>
            {initial}
        </span>
    );
};

const SaverOption = ({ user }) => {
    const hasAvatar = !!user.avatar;

    return (
        <li className="social_proof_option">
            <a
                href={`/user/${user.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="social_proof_option_link"
            >
                {hasAvatar ? (
                    <img
                        className="social_proof_option_avatar"
                        src={user.avatar}
                        alt=""
                    />
                ) : (
                    <span className="social_proof_option_avatar social_proof_option_avatar--initials">
                        {(user.first_name || user.full_name || "?").charAt(0).toUpperCase()}
                    </span>
                )}
                <span className="social_proof_option_name">
                    {user.full_name}
                    {user.is_verified && <CertificateIcon className="certificate_icon--small" />}
                </span>
            </a>
        </li>
    );
};

const SaverGroup = ({ icon, title, count, users, emptyText }) => (
    <section className="social_proof_group">
        <h4 className="social_proof_group_title">
            <FontAwesomeIcon icon={icon} />
            <span>{title}</span>
            <span className="social_proof_group_count">{count}</span>
        </h4>

        {users.length > 0 ? (
            <ul className="social_proof_list">
                {users.map((user) => (
                    <SaverOption key={user.id} user={user} />
                ))}
            </ul>
        ) : (
            <p className="social_proof_group_empty">{emptyText}</p>
        )}
    </section>
);

/**
 * Společné uložení (social proof).
 *
 * Kompaktní řádek: max 3 avatary + kolečko s počty zbylých.
 * Pokud je `expandable` (profily), kliknutí rozbalí seznam se skupinami
 * "Ve vaší síti" a "Ostatní uživatelé".
 *
 * `position="overlay"` – umístí se jako absolutní overlay
 *   (použito pro dlaždice v seznamu pod obrázkem).
 *
 * `total === 0` – místo nic neukazovat, zobrazí se text "Zatím nikdo".
 */
const SocialProof = ({
    stats,
    loading,
    expandable = false,
    position = "inline",
}) => {
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);
    const panelId = useId();

    // Kliknutí mimo seznam zavře dropdown
    useEffect(() => {
        if (!open) return undefined;

        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [open]);

    if (loading) {
        return (
            <div className="social_proof social_proof--loading" aria-hidden="true">
                <span className="social_proof_skeleton" />
            </div>
        );
    }

    if (!stats) {
        return null;
    }

    const mutualUsers = stats.mutual_users || [];
    const otherUsers = stats.other_users || [];
    const mutualCount = stats.mutual_count || 0;
    const otherCount = stats.other_count ?? 0;
    const total = stats.total_saved;

    // total === 0 → "Zatím nikdo"
    if (total === 0) {
        return (
            <div
                className={`social_proof social_proof--empty ${position === "overlay" ? "social_proof--overlay" : ""} ${position === "user_more_info" ? "social_proof--empty--user_more_info" : ""} ${position === "user_basic_info" ? "social_proof--empty--user_basic_info" : ""}`}
                aria-hidden="true"
            >
                <span className={`${position === "overlay" ? "social_proof--overlay-hidden_child" : ""}`}>Uložili si:</span>
                <span className={`${position === "overlay" ? "social_proof--overlay-hidden_child" : ""}`}>Zatím nikdo</span>
            </div>
        );
    }

    const stackUsers = [...mutualUsers, ...otherUsers].slice(0, MAX_AVATARS);
    const remaining = total - stackUsers.length;
    const hiddenMutual = Math.max(mutualCount - mutualUsers.length, 0);
    const hiddenOther = Math.max(otherCount - otherUsers.length, 0);

    const toggleOpen = () => setOpen((prev) => !prev);

    const Trigger = (
        <div
            type="button"
            className={`${position === "user_more_info" ? "social_proof_trigger" : ""} ${position === "user_basic_info" ? "social_proof_trigger_compact" : ""} ${position === "overlay" ? "social_proof_trigger_static" : ""}`}
            onClick={expandable ? toggleOpen : undefined}
            aria-expanded={expandable ? open : undefined}
            aria-controls={expandable ? panelId : undefined}
        >
            <span className={`social_proof_since_label_compact ${position === "overlay" ? "social_proof--overlay-hidden_child" : ""}`}>Uložili si:</span>
            {stackUsers.length > 0 && (
                <span className="social_proof_avatar_stack">
                    {stackUsers.map((user, index) => (
                        user.avatar ? (
                            <img
                                key={user.id}
                                className="social_proof_avatar"
                                src={user.avatar}
                                alt=""
                                title={user.full_name}
                                style={{ zIndex: stackUsers.length - index }}
                            />
                        ) : (
                            <InitialsAvatar key={user.id} user={user} style={{ zIndex: stackUsers.length - index }} />
                        )
                    ))}
                </span>
            )}

            {remaining > 0 && (
                <span className="social_proof_remaining_badge">+{remaining}</span>
            )}
        </div>
    );

    const content = (
        <>
            {expandable ? Trigger : <div className={`${position === "user_basic_info" ? "social_proof_trigger_compact" : ""}`}>{Trigger}</div>}

            {expandable && open && (
                <div className="social_proof_dropdown" id={panelId}>
                    <SaverGroup
                        icon={faUserFriends}
                        title="Vámi uložení"
                        count={mutualCount}
                        users={mutualUsers}
                        emptyText="Nikoho zde ze své sítě zatím nemáte."
                    />

                    <SaverGroup
                        icon={faUsers}
                        title="Ostatní"
                        count={otherCount}
                        users={otherUsers}
                        emptyText="Všichni, kdo si pracovníka uložili, jsou Vámi uložení."
                    />

                    {(hiddenMutual > 0 || hiddenOther > 0) && (
                        <p className="social_proof_panel_note">
                            {[
                                hiddenMutual > 0
                                    ? `${hiddenMutual} dalších z vaší síti`
                                    : null,
                                hiddenOther > 0
                                    ? `${hiddenOther} dalších mimo síť`
                                    : null,
                            ]
                                .filter(Boolean)
                                .join(" · ")}
                        </p>
                    )}
                </div>
            )}
        </>
    );

    return (
        <div
            className={`social_proof ${position === "overlay" ? "social_proof--overlay" : ""}`}
            ref={containerRef}
        >
            {content}
        </div>
    );
};

export default SocialProof;
